<template>
  <div class="kds-page">
    <!-- Top bar -->
    <div class="kds-topbar">
      <div class="kds-topbar-left">
        <button class="kds-icon-btn" @click="goBack" :title="$t('back')">
          <v-icon color="white">mdi-arrow-left</v-icon>
        </button>
        <v-icon color="white" size="26" class="mx-2">mdi-chef-hat</v-icon>
        <div class="kds-title-block">
          <div class="kds-title">{{ $t("kitchen_display") }}</div>
          <div class="kds-subtitle">
            {{ restaurantName || $t("kitchen_display_subtitle") }}
          </div>
        </div>
      </div>

      <div class="kds-topbar-center">
        <button
          v-for="tab in statusTabs"
          :key="tab.value"
          class="kds-status-tab"
          :class="{ active: statusView === tab.value }"
          :style="statusView === tab.value ? { backgroundColor: tab.accent } : {}"
          @click="statusView = tab.value"
        >
          <v-icon small class="mr-1">{{ tab.icon }}</v-icon>
          <span>{{ tab.label }}</span>
          <span class="kds-status-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <div class="kds-topbar-right">
        <div class="kds-date-control">
          <button
            class="kds-filter-chip kds-date-chip"
            :class="{ active: dateMode === 'last24h' }"
            @click="setLast24hMode"
          >
            <v-icon small class="mr-1">mdi-history</v-icon>
            {{ $t("last_24h") }}
          </button>
          <div class="kds-date-input-wrap" :class="{ active: dateMode === 'custom' }">
            <button class="kds-date-arrow" @click="shiftCustomDate(-1)">
              <v-icon x-small color="white">mdi-chevron-left</v-icon>
            </button>
            <input
              type="date"
              class="kds-date-input"
              :value="customDate"
              @input="onCustomDateChange($event.target.value)"
            />
            <button class="kds-date-arrow" @click="shiftCustomDate(1)">
              <v-icon x-small color="white">mdi-chevron-right</v-icon>
            </button>
          </div>
        </div>
        <div class="kds-clock">
          <div class="kds-clock-time">{{ clockText }}</div>
          <div class="kds-clock-date">{{ clockDateText }}</div>
        </div>
        <button
          class="kds-icon-btn"
          :class="{ spinning: isLoading }"
          @click="loadTickets"
          :title="$t('tooltip_refresh_report')"
        >
          <v-icon color="white">mdi-refresh</v-icon>
        </button>
        <v-menu offset-y left :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <button
              class="kds-icon-btn"
              v-bind="attrs"
              v-on="on"
              :title="$t('kds_density_settings')"
            >
              <v-icon color="white">mdi-tune-variant</v-icon>
            </button>
          </template>
          <v-card class="kds-settings-card" dark>
            <v-card-title class="kds-settings-title">{{
              $t("kds_density_settings")
            }}</v-card-title>
            <v-card-text>
              <div v-for="group in kdsGroupDefs" :key="group.key" class="kds-settings-row">
                <div class="kds-settings-label">
                  <v-icon small :color="group.accent" class="mr-2">{{ group.icon }}</v-icon>
                  {{ groupLabel(group.key) }}
                </div>
                <v-btn-toggle
                  v-model="densitySettings[group.key]"
                  mandatory
                  dense
                  color="primary"
                  class="kds-settings-toggle"
                >
                  <v-btn small value="full">{{ $t("kds_density_full") }}</v-btn>
                  <v-btn small value="compact">{{ $t("kds_density_compact") }}</v-btn>
                  <v-btn small value="minimal">{{ $t("kds_density_minimal") }}</v-btn>
                </v-btn-toggle>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
        <button
          class="kds-icon-btn"
          @click="toggleFullscreen"
          :title="$t('kitchen_display')"
        >
          <v-icon color="white">{{
            isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen"
          }}</v-icon>
        </button>
      </div>
    </div>

    <!-- Secondary bar: delivery type filter -->
    <div class="kds-subbar">
      <button
        v-for="filter in typeFilters"
        :key="filter.value"
        class="kds-filter-chip"
        :class="{ active: activeTypeFilter === filter.value }"
        @click="setTypeFilter(filter.value)"
      >
        <v-icon small class="mr-1">{{ filter.icon }}</v-icon>
        <span class="kds-filter-label">{{ filter.label }}</span>
        <span class="kds-filter-count">{{ filter.count }}</span>
      </button>
    </div>

    <!-- Board -->
    <div class="kds-board-wrap">
      <div v-if="statusView === 'all'" class="kds-columns">
        <kds-column
          v-for="group in kdsGroupDefs"
          :key="group.key"
          :title="groupLabel(group.key)"
          :icon="group.icon"
          :accent-color="group.accent"
          :tickets="groupedTickets[group.key]"
          :density="densitySettings[group.key]"
          :order-statuses="orderStatuses"
          :changing-id="changingId"
          @advance="(ticket) => updateStatus(ticket, ticket.nextStatus)"
          @change-status="(ticket, status) => updateStatus(ticket, status)"
          @open-details="openDetails"
        />
      </div>

      <div v-else class="kds-columns kds-columns--single">
        <kds-column
          :title="groupLabel(statusView)"
          :icon="activeGroupDef.icon"
          :accent-color="activeGroupDef.accent"
          :tickets="groupedTickets[statusView]"
          :density="densitySettings[statusView]"
          :order-statuses="orderStatuses"
          :changing-id="changingId"
          @advance="(ticket) => updateStatus(ticket, ticket.nextStatus)"
          @change-status="(ticket, status) => updateStatus(ticket, status)"
          @open-details="openDetails"
        />
      </div>
    </div>

    <guest-order-details-dialog
      v-model="detailsDialog"
      :order="detailsOrder"
      :guest="detailsGuest"
      :changing-status-order-id="changingId"
      @change-status="onDialogChangeStatus"
    />
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasSnackbar from "@/mixins/hasSnackbar";
import KdsColumn from "./kds/KdsColumn.vue";
import GuestOrderDetailsDialog from "@/components/dialogs/GuestOrderDetailsDialog.vue";
import {
  DELIVERY_TYPE_META,
  STATUS_COLORS,
  NEXT_STATUS,
  ACTIVE_STATES,
  KDS_GROUPS,
  toISODate,
  groupForState,
} from "./kds/kdsConstants";

const DENSITY_STORAGE_KEY = "kds-density-settings";

export default {
  name: "KitchenDisplay",
  components: { KdsColumn, GuestOrderDetailsDialog },
  mixins: [hasSnackbar],
  data() {
    return {
      reportData: {},
      isLoading: false,
      activeTypeFilter: "all",
      statusView: "all",
      densitySettings: this.loadDensitySettings(),
      dateMode: "last24h",
      customDate: toISODate(new Date()),
      changingId: null,
      nowTs: Date.now(),
      isFullscreen: false,
      refreshTimer: null,
      clockTimer: null,
      detailsDialog: false,
      detailsOrder: null,
      detailsGuest: null,
      orderStatuses: [
        { value: "pending", icon: "mdi-clock-outline" },
        { value: "accepted", icon: "mdi-check" },
        { value: "in-progress", icon: "mdi-progress-clock" },
        { value: "ready", icon: "mdi-food" },
        { value: "on-hold", icon: "mdi-pause" },
        { value: "delivered", icon: "mdi-truck-check" },
        { value: "completed", icon: "mdi-check-all" },
        { value: "cancelled", icon: "mdi-cancel" },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    adminRestaurantIds() {
      if (this.user?.type !== "admin" || !this.user?.restaurants) return "";
      return this.user.restaurants
        .filter((item) => item.settings && item.settings.lice_bez_ugovora)
        .map((item) => item.id)
        .join(",");
    },
    restaurantName() {
      return this.toLatin(this.$store.state.restaurant?.name || "");
    },
    clockText() {
      return new Date(this.nowTs).toLocaleTimeString("sr-Latn", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    clockDateText() {
      return new Date(this.nowTs).toLocaleDateString("sr-Latn", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    dateRangeParams() {
      if (this.dateMode === "last24h") {
        const now = new Date();
        return {
          date: toISODate(new Date(now.getTime() - 24 * 60 * 60 * 1000)),
          date2: toISODate(now),
        };
      }

      return { date: this.customDate, date2: this.customDate };
    },
    dateRangeKey() {
      return `${this.dateRangeParams.date}|${this.dateRangeParams.date2}`;
    },
    kdsGroupDefs() {
      return KDS_GROUPS;
    },
    activeGroupDef() {
      return KDS_GROUPS.find((g) => g.key === this.statusView) || KDS_GROUPS[0];
    },
    allTickets() {
      const tickets = [];
      const cutoffTs = this.nowTs - 24 * 60 * 60 * 1000;

      Object.values(this.reportData || {}).forEach((companyData) => {
        Object.values(companyData || {}).forEach((guests) => {
          (guests || []).forEach((guest) => {
            const order = guest.orders_guest && guest.orders_guest[0];
            if (!order || !ACTIVE_STATES.includes(order.state)) return;

            const meta = DELIVERY_TYPE_META[order.delivery_type] || DELIVERY_TYPE_META[2];
            const createdAt = order.created_at ? new Date(order.created_at).getTime() : this.nowTs;

            if (this.dateMode === "last24h" && createdAt < cutoffTs) return;

            const firstName = this.toLatin(guest.name || "");
            const lastName = this.toLatin(guest.lastname || "");

            tickets.push({
              id: order.id,
              state: order.state,
              deliveryType: order.delivery_type,
              typeIcon: meta.icon,
              typeLabel: this.$t(meta.labelKey),
              guestName: `${firstName} ${lastName}`.trim(),
              guestShort: `${firstName} ${lastName.charAt(0)}${lastName ? "." : ""}`.trim(),
              createdAtTs: createdAt,
              elapsedMinutes: Math.max(0, Math.round((this.nowTs - createdAt) / 60000)),
              nextStatus:
                order.state === "ready"
                  ? order.delivery_type === 0
                    ? "delivered"
                    : "completed"
                  : NEXT_STATUS[order.state] || null,
              items: (order.order_guest_transactions || []).map((t) => ({
                quantity: t.quantity,
                name: this.toLatin((t.food && t.food.name) || (t.meals && t.meals.name) || ""),
              })),
              rawOrder: order,
              rawGuest: guest,
            });
          });
        });
      });

      tickets.sort((a, b) => a.createdAtTs - b.createdAtTs);
      return tickets;
    },
    typeFilters() {
      const counts = { all: this.allTickets.length, 0: 0, 1: 0, 2: 0 };
      this.allTickets.forEach((t) => {
        counts[t.deliveryType] = (counts[t.deliveryType] || 0) + 1;
      });

      return [
        { value: "all", label: this.$t("all"), icon: "mdi-view-grid-outline", count: counts.all },
        {
          value: 0,
          label: this.$t("delivery_to_address"),
          icon: DELIVERY_TYPE_META[0].icon,
          count: counts[0] || 0,
        },
        {
          value: 1,
          label: this.$t("pickup"),
          icon: DELIVERY_TYPE_META[1].icon,
          count: counts[1] || 0,
        },
        {
          value: 2,
          label: this.$t("consumption_in_restaurant"),
          icon: DELIVERY_TYPE_META[2].icon,
          count: counts[2] || 0,
        },
      ];
    },
    filteredTickets() {
      if (this.activeTypeFilter === "all") return this.allTickets;
      return this.allTickets.filter((t) => t.deliveryType === this.activeTypeFilter);
    },
    groupedTickets() {
      const groups = { pending: [], preparing: [], ready: [] };
      this.filteredTickets.forEach((ticket) => {
        const key = groupForState(ticket.state);
        if (key) groups[key].push(ticket);
      });
      return groups;
    },
    statusTabs() {
      return [
        {
          value: "pending",
          label: this.$t("kds_new_orders"),
          icon: "mdi-receipt-text-outline",
          accent: STATUS_COLORS.pending,
          count: this.groupedTickets.pending.length,
        },
        {
          value: "preparing",
          label: this.$t("in-progress"),
          icon: "mdi-pot-steam-outline",
          accent: STATUS_COLORS["in-progress"],
          count: this.groupedTickets.preparing.length,
        },
        {
          value: "ready",
          label: this.$t("ready"),
          icon: "mdi-food-takeout-box-outline",
          accent: STATUS_COLORS.ready,
          count: this.groupedTickets.ready.length,
        },
        {
          value: "all",
          label: this.$t("kds_view_all"),
          icon: "mdi-view-column-outline",
          accent: "#3b5bfd",
          count: this.filteredTickets.length,
        },
      ];
    },
  },
  watch: {
    densitySettings: {
      handler(value) {
        try {
          localStorage.setItem(DENSITY_STORAGE_KEY, JSON.stringify(value));
        } catch (error) {
          console.error("Error saving kitchen display density settings:", error);
        }
      },
      deep: true,
    },
    dateRangeKey: {
      handler() {
        this.loadTickets();
      },
    },
  },
  mounted() {
    this.loadTickets();
    this.refreshTimer = setInterval(this.loadTickets, 30000);
    this.clockTimer = setInterval(() => {
      this.nowTs = Date.now();
    }, 1000);
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
    clearInterval(this.clockTimer);
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
  },
  methods: {
    loadDensitySettings() {
      const defaults = { pending: "full", preparing: "full", ready: "full" };
      try {
        const stored = JSON.parse(localStorage.getItem(DENSITY_STORAGE_KEY) || "null");
        if (stored) return { ...defaults, ...stored };
      } catch (error) {
        console.error("Error reading kitchen display density settings:", error);
      }
      return defaults;
    },
    groupLabel(key) {
      if (key === "pending") return this.$t("kds_new_orders");
      if (key === "preparing") return this.$t("in-progress");
      if (key === "ready") return this.$t("ready");
      return "";
    },
    setTypeFilter(value) {
      this.activeTypeFilter = value;
    },
    goBack() {
      this.$router.push({ name: "guest-report" });
    },
    setLast24hMode() {
      this.dateMode = "last24h";
    },
    onCustomDateChange(value) {
      if (!value) return;
      this.customDate = value;
      this.dateMode = "custom";
    },
    shiftCustomDate(days) {
      const base = new Date(`${this.customDate}T00:00:00`);
      base.setDate(base.getDate() + days);
      this.customDate = toISODate(base);
      this.dateMode = "custom";
    },
    loadTickets() {
      if (this.user?.type === "admin" && !this.adminRestaurantIds) return;

      this.isLoading = true;

      const request =
        this.user?.type === "admin"
          ? reportService.getCompanyMultipleNoContract({
              ...this.dateRangeParams,
              restaurants: this.adminRestaurantIds,
            })
          : reportService.getNoContractDelivery(
              this.$store.state.restaurant?.id,
              this.dateRangeParams
            );

      request
        .then((response) => {
          this.reportData = response.data || {};
        })
        .catch((error) => {
          console.error("Error loading kitchen display data:", error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async updateStatus(ticket, newStatus) {
      if (!newStatus || ticket.state === newStatus) return;

      this.changingId = ticket.id;

      const payload = {
        ...ticket.rawOrder,
        order_id: ticket.rawOrder.id,
        user_id: ticket.rawGuest.id,
        status: newStatus,
      };

      try {
        await reportService.changeOrderStatus(payload);
        ticket.rawOrder.state = newStatus;
        this.showSuccessSnackbar(this.$t("status_changed_successfully"));
      } catch (error) {
        console.error("Error changing order status:", error);
        this.showErrorSnackbar(this.$t("status_change_error"));
      } finally {
        this.changingId = null;
      }
    },
    openDetails(ticket) {
      this.detailsOrder = ticket.rawOrder;
      this.detailsGuest = ticket.rawGuest;
      this.detailsDialog = true;
    },
    onDialogChangeStatus({ order, guest, newStatus }) {
      this.updateStatus(
        { rawOrder: order, rawGuest: guest, id: order.id, state: order.state },
        newStatus
      );
    },
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      } else {
        document.exitFullscreen().catch(() => {});
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
  },
};
</script>

<style scoped>
.kds-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background: #14171f;
  overflow: hidden;
}

.kds-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  background: #1c2030;
  border-bottom: 1px solid #2a2f42;
  flex-wrap: wrap;
  gap: 8px;
}

.kds-topbar-left {
  display: flex;
  align-items: center;
}

.kds-title-block {
  line-height: 1.15;
}

.kds-title {
  color: #fff;
  font-size: 20px;
  font-weight: 700;
}

.kds-subtitle {
  color: #9aa0b4;
  font-size: 12px;
}

.kds-topbar-center {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.kds-status-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #333952;
  background: #232842;
  color: #cfd3e6;
  border-radius: 20px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.kds-status-tab.active {
  color: #fff;
  border-color: transparent;
}

.kds-status-tab-count {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  padding: 0 7px;
  font-size: 12px;
}

.kds-subbar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 18px;
  background: #181c2a;
  border-bottom: 1px solid #2a2f42;
}

.kds-filter-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #333952;
  background: #232842;
  color: #cfd3e6;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.kds-filter-chip.active {
  background: #3b5bfd;
  border-color: #3b5bfd;
  color: #fff;
}

.kds-filter-count {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 0 7px;
  font-size: 12px;
}

.kds-topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.kds-date-control {
  display: flex;
  align-items: center;
  gap: 6px;
}

.kds-date-chip {
  white-space: nowrap;
}

.kds-date-input-wrap {
  display: flex;
  align-items: center;
  gap: 2px;
  border: 1px solid #333952;
  background: #232842;
  border-radius: 20px;
  padding: 2px;
}

.kds-date-input-wrap.active {
  border-color: #3b5bfd;
}

.kds-date-arrow {
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.kds-date-input {
  background: transparent;
  border: none;
  color: #cfd3e6;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 2px;
  width: 122px;
  color-scheme: dark;
}

.kds-date-input:focus {
  outline: none;
}

.kds-clock {
  text-align: right;
  color: #fff;
  margin-right: 4px;
}

.kds-clock-time {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.kds-clock-date {
  font-size: 11px;
  color: #9aa0b4;
}

.kds-icon-btn {
  background: #232842;
  border: 1px solid #333952;
  border-radius: 8px;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.kds-icon-btn.spinning .v-icon {
  animation: kds-spin 0.8s linear infinite;
}

@keyframes kds-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.kds-settings-card {
  background: #1c2030 !important;
  min-width: 340px;
}

.kds-settings-title {
  font-size: 14px;
  font-weight: 700;
}

.kds-settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #2a2f42;
}

.kds-settings-row:last-child {
  border-bottom: none;
}

.kds-settings-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #e6e8f2;
  white-space: nowrap;
}

.kds-settings-toggle {
  flex-shrink: 0;
}

.kds-board-wrap {
  flex: 1;
  padding: 16px;
  overflow: hidden;
  min-height: 0;
}

.kds-columns {
  display: flex;
  gap: 16px;
  height: 100%;
}

.kds-columns > * {
  flex: 1;
  min-width: 0;
}

.kds-columns--single {
  display: block;
  height: 100%;
}
</style>
